import { acquisitionAmountToNumber } from '../acquisitionAmount';
import { CDQ_ASKS, CdqFormValues } from './cdqForm';
import { cdqAgeToNumber } from './cdqValidation';

/**
 * Why a compiled profile cannot become a CdQ lead. These are the backend's own
 * `details.reason` identifiers: the browser evaluates the same policy only so
 * the operator learns the answer while the customer is still on the phone, and
 * `422 ineligible_profile` is explained with this very map. A reason the two
 * sides spell differently would be a divergence nobody could see.
 */
export type CdqIneligibilityReason =
  | 'pensioner_over_85'
  | 'insufficient_pension'
  | 'non_permanent_employment'
  | 'short_tenure_small_company'
  | 'short_tenure_large_company';

/**
 * What the operator reads. Each sentence says which answer decided it and that
 * the lead is not acquired: there is no override here, because the backoffice
 * does not get to run an underwriting policy of its own.
 */
export const CDQ_INELIGIBILITY_MESSAGE: Record<CdqIneligibilityReason, string> =
  {
    pensioner_over_85:
      'Un pensionato di più di 85 anni non rientra nei requisiti di età della Cessione del quinto. Il profilo non è idoneo e la lead non può essere acquisita.',
    insufficient_pension:
      'Una pensione mensile di €700 o meno non raggiunge il reddito minimo previsto per una Cessione del quinto. Il profilo non è idoneo e la lead non può essere acquisita.',
    non_permanent_employment:
      'Senza un contratto a tempo indeterminato il profilo non è idoneo per una Cessione del quinto. La lead non può essere acquisita.',
    short_tenure_small_company:
      'In un’azienda con meno di 15 dipendenti servono almeno 3 anni di anzianità: con 1 o 2 anni il profilo non è idoneo per una Cessione del quinto e la lead non può essere acquisita.',
    short_tenure_large_company:
      'In un’azienda con più di 15 dipendenti serve più di 1 anno di anzianità: con 1 anno o meno il profilo non è idoneo per una Cessione del quinto e la lead non può essere acquisita.',
  };

/** A pensioner must not exceed this age; 85 itself still qualifies. */
const CDQ_PENSIONER_AGE_CEILING = 85;

/** A pension must exceed this to qualify; €700 itself does not. */
const CDQ_PENSION_FLOOR = 700;

/**
 * The answers the qualification policy reads. It is a subset of the draft on
 * purpose: nothing about the customer's identity, contacts or residence can
 * change the outcome, so none of it is in scope here.
 */
export type CdqEligibilityAnswers = Pick<
  CdqFormValues,
  | 'occupazione'
  | 'anni'
  | 'redditoMensilePensionato'
  | 'indeterminato'
  | 'tempo'
  | 'nDipendenti'
>;

/**
 * The public product's qualification policy, mirrored. It answers only for
 * answers that are actually there: a half-compiled draft is not yet ineligible,
 * it is unfinished, and saying otherwise would reject a customer over a
 * question the operator has not asked yet.
 *
 * Reasons are decided in the order the public funnel asks the questions, so the
 * explanation names the first answer that closed the door rather than an
 * arbitrary one.
 */
export const cdqIneligibility = (
  answers: CdqEligibilityAnswers
): CdqIneligibilityReason | null => {
  if (answers.occupazione === 'pensionato') {
    const age = cdqAgeToNumber(answers.anni);
    if (age !== null && age > CDQ_PENSIONER_AGE_CEILING) {
      return 'pensioner_over_85';
    }

    const pension = acquisitionAmountToNumber(answers.redditoMensilePensionato);
    if (pension !== null && pension <= CDQ_PENSION_FLOOR) {
      return 'insufficient_pension';
    }

    return null;
  }

  // Everyone who still works has to work permanently; the question is not
  // asked at all until the operator has said what the customer does.
  if (answers.occupazione && answers.indeterminato === 'no') {
    return 'non_permanent_employment';
  }

  // How long the customer has been there only matters against an employer size,
  // and only some employers are asked for one: a public employer has no size in
  // this policy, so its employees never meet this rejection. The branch is
  // described once, by the question's own definition.
  if (!CDQ_ASKS.nDipendenti(answers) || !answers.nDipendenti) return null;

  if (answers.nDipendenti === 'Meno di 15 dip') {
    return answers.tempo === '1 anno o meno' || answers.tempo === '2 anni'
      ? 'short_tenure_small_company'
      : null;
  }

  return answers.tempo === '1 anno o meno'
    ? 'short_tenure_large_company'
    : null;
};
